/**
 * L'entonnoir d'entrée du studio : la carte de bienvenue, l'écran vide, la
 * porte franchie — ou le silence.
 *
 * Volontairement **hors de React**. Ce qui fait la difficulté de cet écran
 * n'est pas d'émettre un événement, c'est de ne pas l'émettre deux fois : une
 * exposition est un état qui dure, un rendu est un instant, et confondre les
 * deux transforme un écran d'accueil en compteur de rendus. La machine ci-
 * dessous tient l'exposition courante et refuse tout ce qui la répéterait ;
 * la couche React ne lui dit que ce qu'elle voit à l'écran.
 *
 * Le puits et l'horloge sont injectables : la non-répétition et les délais se
 * vérifient alors sans DOM, sans faux minuteurs et sans espionner `Date`.
 */
import { enqueue } from "./client";
import type {
  StudioEmptyDismissReason,
  StudioEmptyOrigin,
  StudioEntryDoor,
  VirtuosaEvent,
} from "./events";

/** Ce que la couche React sait de l'écran vide au moment où il s'affiche. */
export type StudioEmptyExposure = {
  readonly origin: StudioEmptyOrigin;
  /**
   * Une reprise était proposée. Retenu jusqu'à la fermeture de l'exposition :
   * l'offre n'a pas de bouton de refus, donc « elle était là et personne ne
   * l'a prise » est la seule trace qu'un refus puisse laisser.
   */
  readonly hasResumeOffer: boolean;
  readonly authenticated: boolean;
};

export type StudioEntryFunnelDeps = {
  readonly emit?: (event: VirtuosaEvent) => void;
  readonly now?: () => number;
};

export type StudioEntryFunnel = {
  /** La carte de bienvenue est sous les yeux. */
  welcomeShown(authenticated: boolean): void;
  /** « Entrer dans le studio » a été pris. */
  entered(): void;
  /** L'écran vide est sous les yeux. */
  emptyShown(exposure: StudioEmptyExposure): void;
  /** L'écran vide ne l'est plus. */
  emptyHidden(reason: StudioEmptyDismissReason): void;
  /** Une des portes de l'écran vide a été prise. */
  doorChosen(door: StudioEntryDoor): void;
};

type Exposure = {
  readonly at: number;
  readonly hasResumeOffer: boolean;
  doorTaken: boolean;
};

export function createStudioEntryFunnel(
  deps: StudioEntryFunnelDeps = {},
): StudioEntryFunnel {
  const emit = deps.emit ?? enqueue;
  const now = deps.now ?? Date.now;

  let welcomeShownAt: number | null = null;
  let entered = false;
  let exposure: Exposure | null = null;

  return {
    welcomeShown(authenticated: boolean): void {
      if (welcomeShownAt !== null) {
        return;
      }

      welcomeShownAt = now();

      emit({ name: "studio_welcome_shown", properties: { authenticated } });
    },

    entered(): void {
      // Un « Entrer » sans carte affichée n'existe pas — le studio ne démarre
      // le jingle que depuis elle. La garde ne protège donc pas un chemin,
      // elle protège le délai : sans point de départ il n'y a rien à mesurer,
      // et un zéro inventé se lirait comme une entrée instantanée.
      if (entered || welcomeShownAt === null) {
        return;
      }

      entered = true;

      emit({
        name: "studio_entered",
        properties: { elapsedMs: now() - welcomeShownAt },
      });
    },

    emptyShown(shown: StudioEmptyExposure): void {
      if (exposure !== null) {
        return;
      }

      exposure = {
        at: now(),
        hasResumeOffer: shown.hasResumeOffer,
        doorTaken: false,
      };

      emit({
        name: "studio_empty_shown",
        properties: {
          origin: shown.origin,
          hasResumeOffer: shown.hasResumeOffer,
          authenticated: shown.authenticated,
        },
      });
    },

    doorChosen(door: StudioEntryDoor): void {
      const current = exposure;

      // Hors exposition, le geste vient d'ailleurs : la barre d'outils, ou un
      // fichier déposé sur un morceau déjà chargé. Ce n'est pas une porte de
      // l'écran d'accueil.
      //
      // Une seule porte par exposition : `elapsedMs` répond à « au bout de
      // combien de temps le premier geste », et un second clic après un
      // aller-retour dans la modale d'import n'est pas un second choix.
      if (current === null || current.doorTaken) {
        return;
      }

      current.doorTaken = true;

      emit({
        name: "studio_entry_chosen",
        properties: { door, elapsedMs: now() - current.at },
      });
    },

    emptyHidden(reason: StudioEmptyDismissReason): void {
      const current = exposure;

      if (current === null) {
        return;
      }

      exposure = null;

      // Une porte franchie n'est pas un silence : l'écran a disparu parce
      // qu'on s'en est servi.
      if (current.doorTaken) {
        return;
      }

      emit({
        name: "studio_empty_dismissed",
        properties: {
          reason,
          elapsedMs: now() - current.at,
          hasResumeOffer: current.hasResumeOffer,
        },
      });
    },
  };
}
